import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import TabPanel from '@mui/lab/TabPanel';
import TabMenu from './util/TabMenu';
import {
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SidePanel from './util/SidePanel';
import SettingsPanelBody from './util/SettingsPanelBody';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faAddressBook } from '@fortawesome/free-solid-svg-icons';
import DataTable, { ColDef, BulkEditField } from './util/DataTable';
import PageToolbar from './util/PageToolbar';
import SearchFilter, { applySearchAndFilters, ActiveFilter } from './util/SearchFilter';
import {
  renderContactName, renderOrgName, renderTypeName, renderActiveStatus,
} from './util/crmRenderers';
import AddCrmContact from './modal/AddCrmContact';
import AddCrmOrganisation from './modal/AddCrmOrganisation';
import AddCrmProject from './modal/AddCrmProject';
import CrmSettings from './modal/CrmSettings';
import CrmProjectCard from './crmComponents/CrmProjectCard';
import CrmProjectDetail from './crmComponents/CrmProjectDetail';

interface CrmProps { access: any }

export default function Crm({ access }: CrmProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState('contacts');
  const contactsToolbarSlot = useRef<HTMLDivElement>(null);
  const orgsToolbarSlot = useRef<HTMLDivElement>(null);
  const [showSettings, setShowSettings] = useState(false);

  const [contactsAll, setContactsAll] = useState<any[]>([]);
  const [contactSearch, setContactSearch] = useState('');
  const [contactFilters, setContactFilters] = useState<ActiveFilter[]>([]);
  const [contactTypes, setContactTypes] = useState<any[]>([]);
  const [contactCustomFields, setContactCustomFields] = useState<any[]>([]);
  const [contactModal, setContactModal] = useState(false);
  const [editContact, setEditContact] = useState<any>(null);

  const [organisations, setOrganisations] = useState<any[]>([]);
  const [orgSearch, setOrgSearch] = useState('');
  const [orgFilters, setOrgFilters] = useState<ActiveFilter[]>([]);
  const [orgModal, setOrgModal] = useState(false);
  const [editOrg, setEditOrg] = useState<any>(null);

  const [projects, setProjects] = useState<any[]>([]);
  const [projectSearch, setProjectSearch] = useState('');
  const [projectTypeFilter, setProjectTypeFilter] = useState('');
  const [projectModal, setProjectModal] = useState(false);
  const [editProject, setEditProject] = useState<any>(null);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [projectTaskCounts, setProjectTaskCounts] = useState<Record<number, number>>({});
  const [projectEventCounts, setProjectEventCounts] = useState<Record<number, number>>({});

  const loadContacts = useCallback(async () => {
    try {
      const { data } = await axios.get('crm/contacts');
      setContactsAll(Array.isArray(data) ? data : data.data ?? []);
    } catch { enqueueSnackbar('Error loading contacts', { variant: 'error' }); }
  }, [enqueueSnackbar]);

  const contactColumns: ColDef[] = useMemo(() => {
    const cfRender = (field: any) => (c: any) => {
      const cfv = c.customValues?.find((v: any) => v.custom_field_id === field.id);
      if (!cfv || (cfv.value === null || cfv.value === undefined)) return <span className="opacity-30">—</span>;
      switch (field.type) {
        case 'checkbox': return <span>{cfv.value ? '✓' : '✗'}</span>;
        case 'date': return <>{String(cfv.value).slice(0, 10)}</>;
        case 'multi_select': {
          const vals = Array.isArray(cfv.value) ? cfv.value : [String(cfv.value)];
          return <>{vals.join(', ')}</>;
        }
        default: return <>{String(cfv.value)}</>;
      }
    };

    const standardCols: ColDef[] = [
      {
        key: 'name', label: t('Name'),
        sortExtract: (c: any) => `${c.firstName ?? ''} ${c.lastName ?? ''}`.toLowerCase(),
        filterType: 'text' as const,
        filterExtract: (c: any) => `${c.firstName ?? ''} ${c.lastName ?? ''}`,
        render: renderContactName,
      },
      {
        key: 'contactType', label: t('ContactType'),
        sortExtract: (c: any) => c.contactType?.name ?? '',
        filterType: 'select' as const,
        filterOptions: contactTypes.map((ct: any) => ({ value: String(ct.id), label: ct.name })),
        filterExtract: (c: any) => String(c.contactType?.id ?? ''),
        render: (c: any) => renderTypeName(c.contactType),
      },
      {
        key: 'organisation', label: t('Organisations'),
        sortExtract: (c: any) => c.organisation?.name ?? '',
        filterType: 'text' as const, filterExtract: (c: any) => c.organisation?.name ?? '',
        render: (c: any) => <>{c.organisation?.name ?? '—'}</>,
      },
      {
        key: 'email', label: t('Email'),
        filterType: 'text' as const, filterExtract: (c: any) => c.email ?? '',
        render: (c: any) => <>{c.email ?? '—'}</>,
      },
      {
        key: 'phone1', label: t('MobilePhone'),
        filterType: 'text' as const, filterExtract: (c: any) => c.phone1 ?? '',
        render: (c: any) => <>{c.phone1 ?? '—'}</>,
      },
      {
        key: 'isActive', label: t('IsActive'),
        sortable: false, align: 'right' as const, width: '8%',
        filterType: 'boolean' as const,
        filterExtract: (c: any) => String(!!c.isActive),
        filterOptions: [{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }],
        render: (c: any) => renderActiveStatus(c.isActive),
      },
      // ── Hidden by default ──
      {
        key: 'middleName', label: t('MiddleName'), defaultVisible: false,
        filterType: 'text' as const, filterExtract: (c: any) => c.middleName ?? '',
        render: (c: any) => <>{c.middleName ?? '—'}</>,
      },
      {
        key: 'phone2', label: t('WorkPhone'), defaultVisible: false,
        filterType: 'text' as const, filterExtract: (c: any) => c.phone2 ?? '',
        render: (c: any) => <>{c.phone2 ?? '—'}</>,
      },
      {
        key: 'jobTitle', label: t('JobTitle'), defaultVisible: false,
        filterType: 'text' as const, filterExtract: (c: any) => c.jobTitle ?? '',
        sortExtract: (c: any) => c.jobTitle ?? '',
        render: (c: any) => <>{c.jobTitle ?? '—'}</>,
      },
      {
        key: 'department', label: t('Department'), defaultVisible: false,
        filterType: 'text' as const, filterExtract: (c: any) => c.department ?? '',
        sortExtract: (c: any) => c.department ?? '',
        render: (c: any) => <>{c.department ?? '—'}</>,
      },
      {
        key: 'gender', label: t('Gender'), defaultVisible: false,
        filterType: 'select' as const,
        filterOptions: ['Male','Female','Non-binary','Transgender','Other','Prefer not to say'].map(v => ({ value: v, label: v })),
        filterExtract: (c: any) => c.gender ?? '',
        render: (c: any) => <>{c.gender ?? '—'}</>,
      },
      {
        key: 'dateOfBirth', label: t('DateOfBirth'), defaultVisible: false, sortable: false,
        render: (c: any) => <>{c.dateOfBirth ? String(c.dateOfBirth).slice(0, 10) : '—'}</>,
      },
      {
        key: 'city', label: t('City'), defaultVisible: false,
        filterType: 'text' as const, filterExtract: (c: any) => c.city ?? '',
        render: (c: any) => <>{c.city ?? '—'}</>,
      },
      {
        key: 'postcode', label: t('Postcode'), defaultVisible: false,
        filterType: 'text' as const, filterExtract: (c: any) => c.postcode ?? '',
        render: (c: any) => <>{c.postcode ?? '—'}</>,
      },
      {
        key: 'addressLine1', label: t('AddressLine1'), defaultVisible: false,
        filterType: 'text' as const, filterExtract: (c: any) => c.addressLine1 ?? '',
        render: (c: any) => <>{c.addressLine1 ?? '—'}</>,
      },
      {
        key: 'addressLine2', label: t('AddressLine2'), defaultVisible: false, sortable: false,
        render: (c: any) => <>{c.addressLine2 ?? '—'}</>,
      },
      {
        key: 'country', label: t('Country'), defaultVisible: false,
        filterType: 'text' as const, filterExtract: (c: any) => c.country?.name ?? '',
        sortExtract: (c: any) => c.country?.name ?? '',
        render: (c: any) => <>{c.country?.name ?? '—'}</>,
      },
      {
        key: 'notes', label: 'Description', defaultVisible: false, sortable: false,
        render: (c: any) => (
          <span className="block max-w-[240px] truncate" title={c.notes ?? ''}>
            {c.notes ?? '—'}
          </span>
        ),
      },
    ];

    const customCols: ColDef[] = contactCustomFields.map((field: any) => ({
      key: `cf_${field.id}`,
      label: field.column_name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
      defaultVisible: false,
      sortable: field.type !== 'image' && field.type !== 'file',
      sortExtract: (c: any) => {
        const cfv = c.customValues?.find((v: any) => v.custom_field_id === field.id);
        return cfv?.value != null ? String(cfv.value) : '';
      },
      filterType: field.type === 'checkbox'
        ? 'boolean' as const
        : field.type === 'select'
        ? 'select' as const
        : 'text' as const,
      filterOptions: field.type === 'checkbox'
        ? [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]
        : field.type === 'select' && Array.isArray(field.options)
        ? field.options.map((o: string) => ({ value: o, label: o }))
        : undefined,
      filterExtract: (c: any) => {
        const cfv = c.customValues?.find((v: any) => v.custom_field_id === field.id);
        if (!cfv?.value) return '';
        return field.type === 'checkbox' ? String(!!cfv.value) : String(cfv.value);
      },
      render: cfRender(field),
    }));

    return [...standardCols, ...customCols];
  }, [contactTypes, contactCustomFields]);

  const contacts = useMemo(
    () => applySearchAndFilters(contactsAll, contactSearch, contactFilters, contactColumns,
      (c: any) => [`${c.firstName ?? ''} ${c.lastName ?? ''}`, c.email, c.phone1, c.organisation?.name, c.contactType?.name].filter(Boolean).join(' ')),
    [contactsAll, contactSearch, contactFilters, contactColumns],
  );

  const contactBulkFields: BulkEditField[] = useMemo(() => {
    const standard: BulkEditField[] = [
      {
        key: 'contactTypeId', label: t('ContactType'), type: 'select',
        options: contactTypes.map((ct: any) => ({ value: ct.id, label: ct.name })),
      },
      {
        key: 'organisationId', label: t('Organisations'), type: 'select',
        options: organisations.map((o: any) => ({ value: o.id, label: o.name })),
      },
      {
        key: 'department', label: t('Department'), type: 'text',
      },
      {
        key: 'gender', label: t('Gender'), type: 'select',
        options: [
          { value: 'Male', label: 'Male' },
          { value: 'Female', label: 'Female' },
          { value: 'Other', label: 'Other' },
          { value: 'Not stated', label: 'Not stated' },
        ],
      },
      {
        key: 'isActive', label: t('Active'), type: 'boolean',
        options: [
          { value: true, label: 'Active' },
          { value: false, label: 'Archived' },
        ],
      },
    ];
    const customBulk: BulkEditField[] = contactCustomFields
      .filter((f: any) => f.type === 'select' || f.type === 'checkbox')
      .map((f: any) => ({
        key: `cf_${f.id}`,
        label: f.column_name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
        type: f.type === 'checkbox' ? 'boolean' as const : 'select' as const,
        options: f.type === 'checkbox'
          ? [{ value: true, label: 'Yes' }, { value: false, label: 'No' }]
          : (f.options ?? []).map((o: string) => ({ value: o, label: o })),
      }));
    return [...standard, ...customBulk];
  }, [contactTypes, organisations, contactCustomFields]);

  const orgColumns: ColDef[] = useMemo(() => {
    const seen = new Map<number, any>();
    organisations.forEach((o: any) => {
      if (o.organisationType) seen.set(o.organisationType.id, o.organisationType);
    });
    const orgTypeOptions = Array.from(seen.values()).map((ot: any) => ({
      value: String(ot.id), label: ot.name,
    }));

    return [
      {
        key: 'name', label: t('Name'),
        sortExtract: (o: any) => o.name?.toLowerCase() ?? '',
        filterType: 'text' as const,
        filterExtract: (o: any) => o.name ?? '',
        render: renderOrgName,
      },
      {
        key: 'organisationType', label: t('OrganisationType'),
        sortExtract: (o: any) => o.organisationType?.name ?? '',
        filterType: 'select' as const,
        filterOptions: orgTypeOptions,
        filterExtract: (o: any) => String(o.organisationType?.id ?? ''),
        render: (o: any) => renderTypeName(o.organisationType),
      },
      {
        key: 'email', label: t('Email'),
        filterType: 'text' as const, filterExtract: (o: any) => o.email ?? '',
        render: (o: any) => <>{o.email ?? '—'}</>,
      },
      {
        key: 'phone1', label: t('MobilePhone'),
        filterType: 'text' as const, filterExtract: (o: any) => o.phone1 ?? '',
        render: (o: any) => <>{o.phone1 ?? '—'}</>,
      },
      {
        key: 'city', label: t('City'),
        filterType: 'text' as const, filterExtract: (o: any) => o.city ?? '',
        render: (o: any) => <>{o.city ?? '—'}</>,
      },
      {
        key: 'isActive', label: t('IsActive'),
        sortable: false, align: 'right' as const, width: '8%',
        filterType: 'boolean' as const,
        filterExtract: (o: any) => String(!!o.isActive),
        filterOptions: [{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }],
        render: (o: any) => renderActiveStatus(o.isActive),
      },
      {
        key: 'website', label: 'Website', defaultVisible: false,
        filterType: 'text' as const, filterExtract: (o: any) => o.website ?? '',
        render: (o: any) => <>{o.website ?? '—'}</>,
      },
      {
        key: 'phone2', label: t('WorkPhone'), defaultVisible: false,
        filterType: 'text' as const, filterExtract: (o: any) => o.phone2 ?? '',
        render: (o: any) => <>{o.phone2 ?? '—'}</>,
      },
      {
        key: 'industry', label: 'Industry', defaultVisible: false,
        filterType: 'text' as const, filterExtract: (o: any) => o.industry ?? '',
        render: (o: any) => <>{o.industry ?? '—'}</>,
      },
      {
        key: 'companyRegNumber', label: t('CompanyRegNumber'), defaultVisible: false, sortable: false,
        render: (o: any) => <>{o.companyRegNumber ?? '—'}</>,
      },
      {
        key: 'vatNumber', label: t('VatNumber'), defaultVisible: false, sortable: false,
        render: (o: any) => <>{o.vatNumber ?? '—'}</>,
      },
      {
        key: 'charityNumber', label: t('CharityNumber'), defaultVisible: false, sortable: false,
        render: (o: any) => <>{o.charityNumber ?? '—'}</>,
      },
      {
        key: 'addressLine1', label: t('AddressLine1'), defaultVisible: false,
        render: (o: any) => <>{o.addressLine1 ?? '—'}</>,
      },
      {
        key: 'postcode', label: t('Postcode'), defaultVisible: false,
        filterType: 'text' as const, filterExtract: (o: any) => o.postcode ?? '',
        render: (o: any) => <>{o.postcode ?? '—'}</>,
      },
      {
        key: 'notes', label: t('Notes'), defaultVisible: false, sortable: false,
        render: (o: any) => (
          <span className="block max-w-[240px] truncate" title={o.notes ?? ''}>
            {o.notes ?? '—'}
          </span>
        ),
      },
    ];
  }, [organisations]);

  const orgsFiltered = useMemo(
    () => applySearchAndFilters(organisations, orgSearch, orgFilters, orgColumns,
      (o: any) => [o.name, o.email, o.phone1, o.organisationType?.name, o.city, o.industry].filter(Boolean).join(' ')),
    [organisations, orgSearch, orgFilters, orgColumns],
  );

  const handleContactBulkEdit = async (ids: (string | number)[], field: string, value: any) => {
    if (field.startsWith('cf_')) {
      const fieldId = parseInt(field.replace('cf_', ''), 10);
      await Promise.all(
        ids.map(id => axios.post('custom-field-values', { custom_field_id: fieldId, entity_id: id, value }))
      );
    } else {
      await axios.patch('crm/contacts/bulk', { ids: ids.map(Number), field, value });
    }
    enqueueSnackbar(`Updated ${ids.length} contacts`, { variant: 'success' });
    await loadContacts();
  };

  const loadOrganisations = useCallback(async () => {
    try {
      const { data } = await axios.get('crm/organisations');
      setOrganisations(Array.isArray(data) ? data : data.data ?? []);
    } catch { enqueueSnackbar('Error loading organisations', { variant: 'error' }); }
  }, [enqueueSnackbar]);

  const orgBulkFields: BulkEditField[] = useMemo(() => {
    const seen = new Map<number, any>();
    organisations.forEach((o: any) => {
      if (o.organisationType) seen.set(o.organisationType.id, o.organisationType);
    });
    return [
      {
        key: 'organisationTypeId', label: t('OrganisationType'), type: 'select',
        options: Array.from(seen.values()).map((ot: any) => ({ value: ot.id, label: ot.name })),
      },
      { key: 'industry', label: 'Industry', type: 'text' },
      { key: 'city', label: t('City'), type: 'text' },
      {
        key: 'isActive', label: t('Active'), type: 'boolean',
        options: [{ value: true, label: 'Active' }, { value: false, label: 'Archived' }],
      },
    ];
  }, [organisations]);

  const handleOrgBulkEdit = async (ids: (string | number)[], field: string, value: any) => {
    await axios.patch('crm/organisations/bulk', { ids: ids.map(Number), field, value });
    enqueueSnackbar(`Updated ${ids.length} organisations`, { variant: 'success' });
    await loadOrganisations();
  };

  const loadProjects = useCallback(async () => {
    try {
      const params: any = {};
      if (projectSearch) params.search = projectSearch;
      if (projectTypeFilter) params.type = projectTypeFilter;
      const { data } = await axios.get('crm/projects', { params });
      const list: any[] = Array.isArray(data) ? data : data.data ?? [];
      setProjects(list);
      // load task/event counts in parallel
      const counts = await Promise.allSettled(
        list.map((p) => Promise.all([
          axios.get(`crm/projects/${p.id}/tasks`).then((r) => r.data.length).catch(() => 0),
          axios.get(`crm/projects/${p.id}/events`).then((r) => r.data.length).catch(() => 0),
        ]))
      );
      const tc: Record<number, number> = {};
      const ec: Record<number, number> = {};
      list.forEach((p, i) => {
        const r = counts[i];
        if (r.status === 'fulfilled') { tc[p.id] = r.value[0]; ec[p.id] = r.value[1]; }
      });
      setProjectTaskCounts(tc);
      setProjectEventCounts(ec);
    } catch { enqueueSnackbar('Error loading projects', { variant: 'error' }); }
  }, [projectSearch, projectTypeFilter, enqueueSnackbar]);

  useEffect(() => {
    (async () => {
      try {
        const [typesRes, cfRes, orgsRes] = await Promise.all([
          axios.get('crm/contact-types'),
          axios.get('fields/custom', { params: { table: 'crm_contact' } }),
          axios.get('crm/organisations'),
        ]);
        setContactTypes(typesRes.data);
        setContactCustomFields(Array.isArray(cfRes.data) ? cfRes.data : []);
        const orgsData = orgsRes.data;
        setOrganisations(Array.isArray(orgsData) ? orgsData : orgsData?.data ?? []);
      } catch {}
    })();
  }, []);

  useEffect(() => { if (tab === 'contacts') loadContacts(); }, [tab, loadContacts]);
  useEffect(() => { if (tab === 'organisations') loadOrganisations(); }, [tab, loadOrganisations]);
  useEffect(() => { if (tab === 'projects') loadProjects(); }, [tab, loadProjects]);

  const deleteContact = async (id: number) => {
    if (!window.confirm(t('AreYouSure') + '?')) return;
    try {
      await axios.delete(`crm/contacts/${id}`);
      enqueueSnackbar('Deleted', { variant: 'success' });
      loadContacts();
    } catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  const deleteOrg = async (id: number) => {
    if (!window.confirm(t('AreYouSure') + '?')) return;
    try {
      await axios.delete(`crm/organisations/${id}`);
      enqueueSnackbar('Deleted', { variant: 'success' });
      loadOrganisations();
    } catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  const deleteProject = async (id: number) => {
    if (!window.confirm(t('AreYouSure') + '?')) return;
    try {
      await axios.delete(`crm/projects/${id}`);
      enqueueSnackbar('Deleted', { variant: 'success' });
      loadProjects();
    } catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  return (
    <div className="section-page">
      <TabMenu
        value={tab}
        onChange={(_, v) => setTab(v)}
        tabs={[
          { label: t('Contacts'),      value: 'contacts' },
          { label: t('Organisations'), value: 'organisations' },
          { label: t('Projects'),      value: 'projects' },
        ]}
        rightElement={
          <Tooltip title={t('Settings')} placement="left">
            <FontAwesomeIcon
              icon={faGear}
              className="transition-transform duration-500 cursor-pointer opacity-60 hover:rotate-180 hover:opacity-90 text-[var(--text-color)] mr-[4px]"
              onClick={() => setShowSettings(true)}
            />
          </Tooltip>
        }
      >

        {/* ── Contacts ── */}
        <TabPanel value="contacts" sx={{ padding: 0 }}>
          <PageToolbar>
            <SearchFilter grow columns={contactColumns} search={contactSearch} onSearch={setContactSearch} filters={contactFilters} onFilters={setContactFilters} />
            <button
              type="button"
              onClick={() => { setEditContact(null); setContactModal(true); }}
              className="btn-primary">
              <AddIcon sx={{ fontSize: 15 }} />
              <span>{t('AddContact')}</span>
            </button>
            <div style={{ flex: 1 }} />
            <div ref={contactsToolbarSlot} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)' }} />
          </PageToolbar>
          <DataTable
            toolbarRef={contactsToolbarSlot}
            rows={contacts}
            columns={contactColumns}
            filterable={false}
            getKey={(c: any) => c.id}
            onEdit={(c: any) => { setEditContact(c); setContactModal(true); }}
            onRowDoubleClick={(c: any) => { setEditContact(c); setContactModal(true); }}
            onDelete={(c: any) => deleteContact(c.id)}
            onDeleteMany={async (ids) => {
              await Promise.all(ids.map((id) => axios.delete(`crm/contacts/${id}`)));
              enqueueSnackbar('Deleted', { variant: 'success' });
              loadContacts();
            }}
            bulkEditFields={contactBulkFields}
            onBulkEdit={handleContactBulkEdit}
            footerIcon={<FontAwesomeIcon icon={faAddressBook} className="text-[16px] opacity-30 text-[var(--text-color)]" />}
            storageKey="crm-contacts-cols"
          />
        </TabPanel>

        {/* ── Organisations ── */}
        <TabPanel value="organisations" sx={{ padding: 0 }}>
          <PageToolbar>
            <SearchFilter grow columns={orgColumns} search={orgSearch} onSearch={setOrgSearch} filters={orgFilters} onFilters={setOrgFilters} />
            <button
              type="button"
              onClick={() => { setEditOrg(null); setOrgModal(true); }}
              className="btn-primary">
              <AddIcon sx={{ fontSize: 15 }} />
              <span>{t('AddOrganisation')}</span>
            </button>
            <div style={{ flex: 1 }} />
            <div ref={orgsToolbarSlot} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)' }} />
          </PageToolbar>
          <DataTable
            toolbarRef={orgsToolbarSlot}
            rows={orgsFiltered}
            columns={orgColumns}
            filterable={false}
            getKey={(o: any) => o.id}
            onEdit={(o: any) => { setEditOrg(o); setOrgModal(true); }}
            onRowDoubleClick={(o: any) => { setEditOrg(o); setOrgModal(true); }}
            onDelete={(o: any) => deleteOrg(o.id)}
            onDeleteMany={async (ids) => {
              await Promise.all(ids.map((id) => axios.delete(`crm/organisations/${id}`)));
              enqueueSnackbar('Deleted', { variant: 'success' });
              loadOrganisations();
            }}
            bulkEditFields={orgBulkFields}
            onBulkEdit={handleOrgBulkEdit}
            storageKey="crm-orgs-cols"
          />
        </TabPanel>

        {/* ── Projects ── */}
        <TabPanel value="projects" sx={{ padding: 0 }}>
          {selectedProject ? (
            <div className="section-scroll">
              <CrmProjectDetail
                project={selectedProject}
                onBack={() => setSelectedProject(null)}
                onEdit={() => { setEditProject(selectedProject); setProjectModal(true); }}
              />
            </div>
          ) : (
            <>
              <PageToolbar>
                <SearchFilter grow columns={[]} search={projectSearch} onSearch={setProjectSearch} filters={[]} onFilters={() => {}} />
                {['', 'commercial', 'internal', 'grant'].map((pt) => (
                  <button type="button" key={pt}
                    onClick={() => setProjectTypeFilter(pt)}
                    className={`text-[12px] px-[10px] py-[3px] rounded-full border transition-colors ${projectTypeFilter === pt ? 'bg-blue-500 text-white border-blue-500' : 'bg-transparent text-[var(--text-color)] border-[var(--border-input)]'}`}>
                    {pt === '' ? 'All' : pt.charAt(0).toUpperCase() + pt.slice(1)}
                  </button>
                ))}
                <div style={{ flex: 1 }} />
                <button type="button"
                  onClick={() => { setEditProject(null); setProjectModal(true); }}
                  className="btn-primary">
                  <AddIcon sx={{ fontSize: 15 }} /> {t('Add')}
                </button>
              </PageToolbar>
              <div className="section-scroll">
                {projects.length === 0 ? (
                  <div className="text-center text-[13px] text-[var(--text-color-calendar-week)] py-[40px]">
                    No projects yet
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-[12px]">
                    {projects.map((p) => (
                      <CrmProjectCard
                        key={p.id}
                        project={p}
                        taskCount={projectTaskCounts[p.id] ?? 0}
                        eventCount={projectEventCounts[p.id] ?? 0}
                        onClick={() => setSelectedProject(p)}
                        onEdit={(e: React.MouseEvent) => { e.stopPropagation(); setEditProject(p); setProjectModal(true); }}
                        onDelete={(e: React.MouseEvent) => { e.stopPropagation(); deleteProject(p.id); }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </TabPanel>
      </TabMenu>

      <AddCrmContact
        open={contactModal}
        onClose={() => setContactModal(false)}
        onSaved={loadContacts}
        editContact={editContact}
      />
      <AddCrmOrganisation
        open={orgModal}
        onClose={() => setOrgModal(false)}
        onSaved={loadOrganisations}
        editOrg={editOrg}
      />
      <AddCrmProject
        open={projectModal}
        onClose={() => setProjectModal(false)}
        onSaved={async () => {
          await loadProjects();
          // refresh selectedProject with updated data if we're in detail view
          if (selectedProject && editProject?.id === selectedProject.id) {
            const { data } = await axios.get(`crm/projects/${selectedProject.id}`).catch(() => ({ data: null }));
            if (data) setSelectedProject(data);
          }
        }}
        editProject={editProject}
      />
      <SidePanel open={showSettings} onClose={() => setShowSettings(false)} width={624}>
        <SettingsPanelBody><CrmSettings /></SettingsPanelBody>
      </SidePanel>
    </div>
  );
}
